import type { TemplateApplicationMappingV1 } from '@seeqdev/api/model/TemplateApplicationMappingV1';
import type { TemplateTableApplicationsOutputListV1 } from '@seeqdev/api/model/TemplateTableApplicationsOutputListV1';

export const normalizeId = (id?: string) => id?.toLowerCase();

type OutputFeatureForApplicationMapping = {
  id?: string;
  canonicalId?: string;
  featureId?: string;
};

/** Identifiers that may appear as featureId on a table application mapping. */
export const getOutputFeatureIdsForApplicationMapping = (
  outputFeature: OutputFeatureForApplicationMapping,
): string[] => {
  const ids = [
    outputFeature.id,
    'featureId' in outputFeature ? outputFeature.featureId : undefined,
    'canonicalId' in outputFeature ? outputFeature.canonicalId : undefined,
  ].filter((id): id is string => !!id);

  return [...new Set(ids)];
};

/** Column ids mapped by any table application for the given template container. */
export const getMappedColumnIdsForTemplateContainer = (
  templateContainerId: string | undefined,
  templateColumns: TemplateTableApplicationsOutputListV1 | undefined,
): Set<string> => {
  const normalizedContainerId = normalizeId(templateContainerId);
  if (!normalizedContainerId || !templateColumns?.applications?.length) {
    return new Set();
  }

  const matchingApplications = templateColumns.applications.filter(
    (application) => normalizeId(application.templateContainerId) === normalizedContainerId,
  );

  const columnIds = matchingApplications.flatMap(
    (application) =>
      application.mappings?.map((mapping) => mapping.columnId).filter((columnId): columnId is string => !!columnId) ??
      [],
  );

  return new Set(columnIds);
};

/**
 * The first mapping that binds the given template feature, matching by canonicalId (stable across template versions)
 * or by featureId. Ids are compared case-insensitively, and a missing id never matches.
 */
export const findMappingForFeature = (
  mappings: TemplateApplicationMappingV1[],
  feature: { id?: string; canonicalId?: string },
): TemplateApplicationMappingV1 | undefined => {
  const featureId = normalizeId(feature.id);
  const featureCanonicalId = normalizeId(feature.canonicalId);
  return mappings.find(
    (mapping) =>
      (featureCanonicalId !== undefined && normalizeId(mapping.featureCanonicalId) === featureCanonicalId) ||
      (featureId !== undefined && normalizeId(mapping.featureId) === featureId),
  );
};

/** Feature-to-column mappings of the first table application for the given template container. */
export const getMappingsForTemplateContainer = (
  templateContainerId: string | undefined,
  templateColumns: TemplateTableApplicationsOutputListV1 | undefined,
): TemplateApplicationMappingV1[] | undefined => {
  const normalizedContainerId = normalizeId(templateContainerId);
  if (!normalizedContainerId) {
    return undefined;
  }

  // TODO: CRAB-66235: Uses only the first application of the container; a table can have several.
  return templateColumns?.applications?.find((app) => normalizeId(app.templateContainerId) === normalizedContainerId)
    ?.mappings;
};

/** Column id mapped to a template output feature in the first table application for the given template container. */
export const getMappedColumnIdForOutputFeatureInTemplateApplication = (
  outputFeature: OutputFeatureForApplicationMapping,
  templateContainerId: string | undefined,
  templateColumns: TemplateTableApplicationsOutputListV1 | undefined,
): string | undefined => {
  const normalizedContainerId = normalizeId(templateContainerId);
  if (!normalizedContainerId || !templateColumns?.applications?.length) {
    return undefined;
  }

  // TODO: CRAB-66235: Uses only the first application of the container; a table can have several.
  const application = templateColumns.applications.find(
    (app) => normalizeId(app.templateContainerId) === normalizedContainerId,
  );

  const featureIdsToMatch = new Set(
    getOutputFeatureIdsForApplicationMapping(outputFeature).map((id) => normalizeId(id)),
  );
  if (!featureIdsToMatch.size) {
    return undefined;
  }

  return application?.mappings?.find(
    (mapping) => !!mapping.featureId && featureIdsToMatch.has(normalizeId(mapping.featureId)!),
  )?.columnId;
};

/**
 * Column ids that are input features, keyed by the (normalized) templateContainerId of the application that
 * consumes them as an input. A column can be the *output* of one template application and an *input* of another
 * (e.g. a Scaling Table output fed into a second, later-applied template), so this must be checked per-application
 * rather than against a single flat list — otherwise the column would incorrectly lose its template-output status
 * for the application that actually produces it.
 */
export type InputFeatureColumnIdsByTemplateContainer = Record<string, string[]>;

/**
 * Stable per-application key: templateContainerId, falling back to templateGuid when absent — templateContainerId
 * is optional on table applications, matching the fallback already used in `tableTemplate.queries.ts`.
 */
export const getTemplateApplicationKey = (app: { templateContainerId?: string; templateGuid?: string }) =>
  normalizeId(app.templateContainerId ?? app.templateGuid);

const isOwnInputFeature = (
  app: { templateContainerId?: string; templateGuid?: string },
  selectedColumnId: string,
  inputFeatureColumnIds: InputFeatureColumnIdsByTemplateContainer | undefined,
) => (inputFeatureColumnIds?.[getTemplateApplicationKey(app) ?? ''] ?? []).includes(selectedColumnId);

export const isColumnPartOfCurrentTemplateApplication = (
  selectedColumnId: string | undefined,
  templateId: string,
  inputFeatureColumnIds: InputFeatureColumnIdsByTemplateContainer | undefined,
  templateColumns: TemplateTableApplicationsOutputListV1,
) => {
  const normalizedTemplateId = normalizeId(templateId);
  return (
    selectedColumnId !== undefined &&
    templateColumns?.applications?.some(
      (app) =>
        normalizeId(app.templateContainerId) === normalizedTemplateId &&
        app.mappings?.some((m) => m.columnId === selectedColumnId) &&
        !isOwnInputFeature(app, selectedColumnId, inputFeatureColumnIds),
    )
  );
};

export const isColumnInOtherTemplate = (
  selectedColumnId: string | undefined,
  templateId: string,
  inputFeatureColumnIds: InputFeatureColumnIdsByTemplateContainer | undefined,
  templateColumns: TemplateTableApplicationsOutputListV1,
) => {
  const normalizedTemplateId = normalizeId(templateId);
  return (
    selectedColumnId !== undefined &&
    templateColumns?.applications?.some(
      (app) =>
        normalizeId(app.templateContainerId) !== normalizedTemplateId &&
        app.mappings?.some((m) => m.columnId === selectedColumnId) &&
        !isOwnInputFeature(app, selectedColumnId, inputFeatureColumnIds),
    )
  );
};
